import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Search, FileSignature, Users, CircleDot } from "lucide-react";
import { useLocaleFormat } from "@/lib/i18n-format";
import { useFilterByOwnerCompany } from "@/owner/OwnerCompanyContext";
import { CompanyBadge } from "@/owner/CompanyBadge";

export const Route = createFileRoute("/_app/contracts/")({
  component: ContractsPage,
});

type Status = "draft" | "sent" | "signed" | "active" | "completed" | "cancelled";

type ContractRow = {
  id: string;
  number: string;
  title: string;
  status: Status;
  amount: number;
  currency: string;
  start_date: string | null;
  end_date: string | null;
  client_id: string;
  company_id: string;
  clients: { name: string } | null;
};

const statusVariant: Record<Status, "default" | "secondary" | "destructive" | "outline"> = {
  draft: "secondary",
  sent: "outline",
  signed: "default",
  active: "default",
  completed: "secondary",
  cancelled: "destructive",
};

function ContractsPage() {
  const { t } = useTranslation();
  const { formatMoney, formatDate } = useLocaleFormat();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState<ContractRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [clientFilter, setClientFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const statusOptions: Status[] = ["draft", "sent", "signed", "active", "completed", "cancelled"];

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("contracts")
        .select(
          "id, number, title, status, amount, currency, start_date, end_date, client_id, company_id, clients(name)",
        )
        .order("created_at", { ascending: false });
      if (error) toast.error(t("pages.common.loadFailed"), { description: error.message });
      else setContracts((data ?? []) as unknown as ContractRow[]);
      setLoading(false);
    })();
  }, [t]);

  const scoped = useFilterByOwnerCompany(contracts);

  const clientOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of scoped) {
      if (c.client_id && c.clients?.name) map.set(c.client_id, c.clients.name);
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [scoped]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = scoped;
    if (clientFilter !== "all") {
      rows = rows.filter((c) => c.client_id === clientFilter);
    }
    if (statusFilter !== "all") {
      rows = rows.filter((c) => c.status === statusFilter);
    }
    if (!q) return rows;
    return rows.filter((c) =>
      [c.number, c.title, c.clients?.name]
        .filter(Boolean)
        .some((v) => v!.toLowerCase().includes(q)),
    );
  }, [scoped, search, clientFilter, statusFilter]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("pages.contracts.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("pages.contracts.subtitle")}</p>
        </div>
        <Button onClick={() => navigate({ to: "/contracts/new" })} className="gap-2">
          <Plus className="h-4 w-4" />
          {t("pages.contracts.new")}
        </Button>
      </div>

      <div className="rounded-lg border border-border bg-card">
        <div className="flex flex-col gap-2 border-b border-border p-3 sm:flex-row sm:items-center">
          <div className="flex flex-1 items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("pages.common.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 border-0 bg-transparent shadow-none focus-visible:ring-0"
            />
          </div>
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger className="h-8 w-full sm:w-[200px]">
              <div className="flex items-center gap-2">
                <Users className="h-3.5 w-3.5 text-muted-foreground" />
                <SelectValue placeholder={t("pages.contracts.headers.client")} />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("pages.common.all", "Todos")}</SelectItem>
              {clientOptions.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 w-full sm:w-[160px]">
              <div className="flex items-center gap-2">
                <CircleDot className="h-3.5 w-3.5 text-muted-foreground" />
                <SelectValue placeholder={t("pages.contracts.headers.status")} />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("pages.common.all", "Todos")}</SelectItem>
              {statusOptions.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`statuses.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Badge variant="secondary" className="sm:ml-2">
            {t("pages.contracts.count", { count: filtered.length })}
          </Badge>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 p-12 text-center">
            <div
              className="flex h-12 w-12 items-center justify-center rounded-full text-primary-foreground"
              style={{ background: "var(--gradient-brand)" }}
            >
              <FileSignature className="h-6 w-6" />
            </div>
            <div>
              <p className="font-medium">
                {contracts.length === 0 ? t("pages.contracts.empty") : t("pages.contracts.noMatch")}
              </p>
              <p className="text-sm text-muted-foreground">
                {contracts.length === 0
                  ? t("pages.contracts.emptyDesc")
                  : t("pages.common.tryDifferentSearch")}
              </p>
            </div>
            {contracts.length === 0 && (
              <Button onClick={() => navigate({ to: "/contracts/new" })} className="gap-2">
                <Plus className="h-4 w-4" />
                {t("pages.contracts.new")}
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("pages.contracts.headers.number")}</TableHead>
                  <TableHead>{t("pages.contracts.headers.client")}</TableHead>
                  <TableHead>{t("pages.contracts.headers.title")}</TableHead>
                  <TableHead>{t("pages.contracts.headers.status")}</TableHead>

                  <TableHead className="text-right">
                    {t("pages.contracts.headers.amount")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c) => (
                  <TableRow key={c.id} className="cursor-pointer">
                    <TableCell className="font-mono text-xs">
                      <Link
                        to="/contracts/$contractId"
                        params={{ contractId: c.id }}
                        className="hover:underline"
                      >
                        {c.number}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <span>{c.clients?.name ?? "—"}</span>
                        <CompanyBadge companyId={c.company_id} />
                      </div>
                    </TableCell>
                    <TableCell>
                      <Link
                        to="/contracts/$contractId"
                        params={{ contractId: c.id }}
                        className="flex items-center gap-2 font-medium hover:underline"
                      >
                        <FileSignature className="h-3.5 w-3.5 text-muted-foreground" />
                        {c.title}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariant[c.status]}>{t(`statuses.${c.status}`)}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatMoney(c.amount, c.currency)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
